'use client';

import Image from 'next/image';
import { useRouter } from 'next/navigation';
import {
  Button,
  Box,
  Divider,
  Typography,
  IconButton,
  Paper,
  Stack,
  Chip,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { FC, useState, useMemo, useCallback } from 'react';
import { useUserInteraction } from '@/hooks/useUnsavedChanges';
import { FormAutocomplete } from '@/components/common/FormComponents';
import { StandardDialog } from '@/components/common/StandardDialog';
import { useSystemConfig } from '@/hooks/useSystemConfig';
import { log } from '@/lib/utils/logger';
import { nullIfBlank } from '@/lib/utils/nullIfBlank';
import { toRecipeTemplateStep } from '@/lib/utils/recipeTemplateStep';
import { filterAutocompleteOptionsByMatchRank } from '@/lib/utils/autocompleteMatchRank';
import { buildTechniqueOptions } from '@/lib/utils/optionUtils';
import { useSelectOptions } from '@/lib/options/useSelectOptions';
import { OptionKey } from '@/lib/options/optionTypes';
import { useBackendStore } from '@/stores/backendStore';
import {
  ProjectDto,
  SampleDto,
  CollaboratorDto,
  RecipeTemplateDto,
  SampleActivityDto,
  AvailableTemplate,
  CombineStartingMaterialsRequest,
  ReportingCategoryDto,
  ActivityTypeDto,
  ActivityCategoryDto,
} from '@/api/ApiClient';
import { isValidRecipeTemplateForStartingMaterials } from '@/lib/utils/recipeUtils';
import { useAuthStore } from '@/stores/authStore';
import { useSnackbar } from '@/hooks/useSnackbar';
import { RichTextEditor } from '@/components/common/RichTextEditor';
import { EntityAutocompleteWithLookup } from '@/components/common/EntityAutocompleteWithLookup';
import { useDialogStore } from '@/stores/dialogStore';
import { IdLabelPair } from '@/types/common';
import { MaterialCreateAddButton } from '@/components/material/MaterialCreateAddButton';
import { MaterialCreateDialog } from '@/components/material/MaterialCreateDialog';
import { useCreateMaterial } from '@/hooks/useCreateMaterial';
import { toNonSubstrateMaterialPicks } from '@/lib/materials/materialOptions';
import { LABEL_SUFFIX_OPTIONAL, LABEL_SUFFIX_REQUIRED } from '@/lib/formLabels';
import { canCreateWithoutProject } from '@/lib/auth/unassignedSampleAccess';
import { getProblemDetail } from '@/lib/utils/apiErrors';
import {
  createProjectResolver,
  enforceProjectMultiplicitySelection,
  sampleProjectMultiplicityFieldHint,
  validateProjectIdsForSampleMultiplicity,
} from '@/lib/utils/sampleProjectMultiplicity';

const OPTION_KEYS: OptionKey[] = [
  'projects',
  'projectType',
  'materials',
  'researcherOptions',
  'staff',
  'sampleDropdownChoices',
  'sampleReportingCategory',
  'synthesisInstruments',
  'synthesisTechniquesLeafOnly',
];

interface CombineStartingMaterialsDialogProps {
  open: boolean;
  onClose: () => void;
  /** If project is provided, this will initialize the project field*/
  project?: ProjectDto | undefined;
}

const CombineStartingMaterialsDialog: FC<CombineStartingMaterialsDialogProps> = ({ open, onClose, project }) => {
  const router = useRouter();
  const systemConfig = useSystemConfig();
  const multiplicityPolicy = systemConfig?.sampleProjectMultiplicity;
  const allowMultiProject = multiplicityPolicy !== 'MaxOneProject';
  const multiplicityHint = sampleProjectMultiplicityFieldHint(multiplicityPolicy);
  const backend = useBackendStore((state) => state.getBackend());
  const { options } = useSelectOptions(OPTION_KEYS);
  const { showProjectSelectionDialog, showSampleSelectionDialog } = useDialogStore();

  // Form states
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [selectedHolder, setSelectedHolder] = useState<string | null>(null);
  const [selectedProjects, setSelectedProjects] = useState<IdLabelPair[]>(
    project ? [{ id: project.id!, label: project.projectLabel }] : []
  );
  const [selectedMaterials, setSelectedMaterials] = useState<IdLabelPair[]>([]);
  const [isMaterialDialogOpen, setIsMaterialDialogOpen] = useState(false);
  const [updatedMaterials, setUpdatedMaterials] = useState<IdLabelPair[] | null>(null);
  const [selectedResearcher, setSelectedResearcher] = useState<CollaboratorDto | null>(null);
  const [selectedReportingCategory, setSelectedReportingCategory] = useState<ReportingCategoryDto | null>(null);
  const [description, setDescription] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  // StartingMaterial-specific states
  const [selectedStartingMaterials, setSelectedStartingMaterials] = useState<SampleDto[]>([]);
  const [processType, setProcessType] = useState<'SYN' | 'PREP'>('SYN');

  // Process-specific states (technique, instrument, recipe)
  const [selectedTechnique, setSelectedTechnique] = useState<{ id: string; name: string } | null>(null);
  const [selectedInstrument, setSelectedInstrument] = useState<{ id: string; name: string } | null>(null);
  const [availableTemplates, setAvailableTemplates] = useState<AvailableTemplate[]>([]);
  const [recipeTemplate, setRecipeTemplate] = useState<RecipeTemplateDto | null>(null);

  // Error states
  const [materialError, setMaterialError] = useState(false);
  const [researcherError, setResearcherError] = useState(false);
  const [projectError, setProjectError] = useState(false);
  const [projectMultiplicityMessage, setProjectMultiplicityMessage] = useState<string | null>(null);
  const [startingMaterialError, setStartingMaterialError] = useState(false);
  const [recipeTemplateError, setRecipeTemplateError] = useState(false);

  // Three-option dialog state for single pristine substrate
  const [pristineChoiceDialogOpen, setPristineChoiceDialogOpen] = useState(false);
  const [pristineSample, setPristineSample] = useState<SampleDto | null>(null);

  // Track user interaction
  const { hasUnsavedChanges, markAsInteracted } = useUserInteraction();
  const { currentUser, isAuthorized } = useAuthStore();
  const allowCreateWithoutProject = canCreateWithoutProject(isAuthorized);
  const { showSnackbar } = useSnackbar();
  const { canCreateMaterial } = useCreateMaterial();

  const projectOptions = useMemo(
    () =>
      Array.from(
        new Map(
          options.projects
            ?.filter((p) => p.id !== undefined && p.projectLabel !== undefined)
            .map((p) => [p.id!, { id: p.id!, label: p.projectLabel! }])
        ).values()
      ),
    [options.projects]
  );

  const resolveProjectDto = createProjectResolver(options.projects, options.projectType, project);

  // File change handler
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      log.debug('[CombineStartingMaterialsDialog] Selected image:', file.name);
      setSelectedFile(file);
      markAsInteracted();
    }
  };

  const handleProjectLookup = useCallback(async () => {
    const projects = await showProjectSelectionDialog(allowMultiProject ? 'Select Projects' : 'Select Project', {
      allowMultiple: allowMultiProject,
    });
    if (projects) {
      const newProjects = projects.map((p) => ({ id: p.id!, label: p.projectLabel! }));
      const applied = allowMultiProject ? newProjects : newProjects.slice(0, 1);
      const resolve = createProjectResolver(options.projects, options.projectType, project);
      const { nextProjects, multiplicityMessage } = enforceProjectMultiplicitySelection(
        multiplicityPolicy,
        applied,
        resolve
      );
      setSelectedProjects(nextProjects);
      if (nextProjects.length > 0) setProjectError(false);
      setProjectMultiplicityMessage(multiplicityMessage);
      markAsInteracted();
    }
  }, [
    allowMultiProject,
    multiplicityPolicy,
    options.projects,
    options.projectType,
    project,
    showProjectSelectionDialog,
    markAsInteracted,
  ]);

  // Sample/StartingMaterial selection handler - adds to existing startingMaterials
  const handleStartingMaterialLookup = async () => {
    const samples = await showSampleSelectionDialog('Select StartingMaterial Sample(s) to Add');

    if (samples && samples.length > 0) {
      // Filter out invalid samples (shipped, split, or already selected)
      const validSamples = samples.filter((sample) => {
        // Exclude shipped samples (they're physically gone)
        if (sample.shipped) {
          return false;
        }

        // Exclude samples that have been split (parent sample - use the pieces instead)
        if (sample.subSamples && sample.subSamples.length > 0) {
          return false;
        }

        // Exclude samples already selected as startingMaterials
        if (selectedStartingMaterials.some((p) => p.id === sample.id)) {
          return false;
        }

        return true;
      });

      if (validSamples.length === 0) {
        showSnackbar(
          'No valid samples selected. Shipped, split, or already-selected samples cannot be used.',
          'warning'
        );
        return;
      }

      if (validSamples.length < samples.length) {
        showSnackbar(
          `${samples.length - validSamples.length} sample(s) were excluded (shipped, split, or already selected).`,
          'info'
        );
      }
      // Show confirmation with what's being added
      const sampleLabels = validSamples.map((s) => s.sampleLabel).join(', ');
      const message =
        selectedStartingMaterials.length > 0
          ? `Adding ${validSamples.length} startingMaterial(s) to your existing ${selectedStartingMaterials.length}:\n${sampleLabels}\n\nOrder matters for recipes that use specific positions.`
          : `Adding ${validSamples.length} startingMaterial(s):\n${sampleLabels}`;

      showSnackbar(message, 'info');

      // Append to existing startingMaterials (order preserved)
      setSelectedStartingMaterials((prev) => [...prev, ...validSamples]);
      markAsInteracted();
    }
  };

  const handleRemoveStartingMaterial = (index: number) => {
    setSelectedStartingMaterials((prev) => prev.filter((_, i) => i !== index));
    markAsInteracted();
  };

  const handleMoveStartingMaterialUp = (index: number) => {
    if (index === 0) return; // Can't move first item up
    setSelectedStartingMaterials((prev) => {
      const newList = [...prev];
      [newList[index - 1], newList[index]] = [newList[index], newList[index - 1]];
      return newList;
    });
    markAsInteracted();
  };

  const handleMoveStartingMaterialDown = (index: number) => {
    setSelectedStartingMaterials((prev) => {
      if (index === prev.length - 1) return prev; // Can't move last item down
      const newList = [...prev];
      [newList[index], newList[index + 1]] = [newList[index + 1], newList[index]];
      return newList;
    });
    markAsInteracted();
  };

  // Material options - use updated materials if available, otherwise use cached options
  const materialOptions = updatedMaterials || toNonSubstrateMaterialPicks(options.materials ?? []);

  // Filtered staff with researcher role
  const filteredStaff = useMemo(() => {
    return options.staff?.filter((s) => s.roles?.some((role) => role.isResearcher)) ?? [];
  }, [options.staff]);

  const defaultResearcher = useMemo(() => {
    if (!currentUser?.id || !filteredStaff.length) return null;
    return filteredStaff.find((r) => r.id === currentUser.id) ?? null;
  }, [currentUser, filteredStaff]);

  const effectiveResearcher = selectedResearcher ?? defaultResearcher;

  // Technique options (leaf-only; filter by researcher's allowed techniques when set)
  const techniqueOptions = useMemo(() => {
    const leaf = options.synthesisTechniquesLeafOnly ?? [];
    const allowedTechniqueIds = effectiveResearcher?.techniques;
    return buildTechniqueOptions(leaf, allowedTechniqueIds);
  }, [options.synthesisTechniquesLeafOnly, effectiveResearcher?.techniques]);

  // Instrument options - filtered by selected technique
  const instrumentOptions = useMemo(() => {
    const base = options.synthesisInstruments ?? [];
    const filtered = selectedTechnique?.id
      ? base.filter((inst) => inst.synthesisTechniques?.includes(selectedTechnique.id))
      : base;
    return filtered
      .filter((m) => m.id !== undefined && m.name !== undefined && m.name !== 'None')
      .sort((a, b) => a.name!.localeCompare(b.name!))
      .map((m) => ({
        id: m.id!,
        name: m.name!,
      }));
  }, [options.synthesisInstruments, selectedTechnique]);

  // Fetch available recipe templates
  // For SYN: requires both technique and instrument
  // For PREP: can fetch with just technique (for templates that don't use instruments)
  const fetchRecipeTemplates = async (
    technique: { id: string; name: string } | null,
    instrument: { id: string; name: string } | null
  ) => {
    if (!technique?.id) {
      setAvailableTemplates([]);
      setRecipeTemplate(null);
      return;
    }

    // For SYN, require instrument. For PREP, instrument is optional
    if (processType === 'SYN' && !instrument?.id) {
      setAvailableTemplates([]);
      setRecipeTemplate(null);
      return;
    }

    try {
      const step = toRecipeTemplateStep(processType) ?? undefined;
      const templates = await backend.recipeTemplate.availableTemplates(step, technique.id, instrument?.id || null);

      setAvailableTemplates(templates);

      // Auto-select if there's only one option
      if (templates.length === 1) {
        const template = templates[0];
        if (template.name) {
          const fullTemplate = await backend.recipeTemplate.get(
            step,
            technique.id,
            instrument?.id || null,
            template.name
          );
          setRecipeTemplate(fullTemplate);
        }
      } else {
        // Multiple options - let user choose
        setRecipeTemplate(null);
      }
    } catch (err) {
      log.error('[AddSampleFromStartingMaterialsDialog] Failed to load recipe templates:', err);
      setAvailableTemplates([]);
      setRecipeTemplate(null);
    }
  };

  // Handler for technique selection
  const handleTechniqueChange = async (
    _event: React.SyntheticEvent,
    newValue: { id: string; name: string } | string | null
  ) => {
    if (typeof newValue === 'string') {
      setSelectedTechnique({ id: '', name: newValue });
      setAvailableTemplates([]);
      setRecipeTemplate(null);
    } else {
      setSelectedTechnique(newValue);
      // Clear instrument when technique changes
      setSelectedInstrument(null);
      setAvailableTemplates([]);
      setRecipeTemplate(null);

      // For PREP: fetch templates immediately when technique is selected
      // Templates that don't use instruments will be available
      if (processType === 'PREP' && newValue?.id) {
        await fetchRecipeTemplates(newValue, null);
      }
    }
    markAsInteracted();
  };

  // Handler for instrument selection
  const handleInstrumentChange = async (
    _event: React.SyntheticEvent,
    newValue: { id: string; name: string } | string | null
  ) => {
    if (typeof newValue === 'string') {
      setSelectedInstrument({ id: '', name: newValue });
      setAvailableTemplates([]);
      setRecipeTemplate(null);
    } else {
      const newInstrument = newValue;
      setSelectedInstrument(newInstrument);

      // Fetch templates when instrument changes (if technique is already set)
      // For PREP: if instrument is cleared, refetch to get templates without instrument requirement
      if (selectedTechnique) {
        await fetchRecipeTemplates(selectedTechnique, newInstrument);
      }
    }
    markAsInteracted();
  };

  // Handler for process type change
  const handleProcessTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setProcessType(e.target.value as 'SYN' | 'PREP');
    // Reset technique/instrument/recipe when changing process type
    setSelectedTechnique(null);
    setSelectedInstrument(null);
    setRecipeTemplate(null);
    setAvailableTemplates([]);
    markAsInteracted();
  };

  // Handler for recipe template selection
  const handleRecipeTemplateChange = async (_event: React.SyntheticEvent, selectedName: string | null) => {
    if (!selectedName || !selectedTechnique?.name) return;

    // Check if the selected template uses an instrument
    const selectedTemplate = availableTemplates.find((t) => t.name === selectedName);
    const usesInstrument = selectedTemplate?.usesInstrument ?? true;

    // If template uses instrument, require instrument selection
    if (usesInstrument && !selectedInstrument?.name) {
      return;
    }

    try {
      const step = toRecipeTemplateStep(processType) ?? undefined;
      const fullTemplate = await backend.recipeTemplate.get(
        step,
        selectedTechnique.name,
        usesInstrument ? selectedInstrument!.name : null,
        selectedName
      );
      setRecipeTemplate(fullTemplate);
      markAsInteracted();
    } catch (error) {
      log.error('[AddSampleFromStartingMaterialsDialog] Failed to fetch selected recipe template:', error);
      setRecipeTemplate(null);
    }
  };

  // Reset all form fields to their initial state
  const handleFormReset = () => {
    setSelectedDate(new Date());
    setSelectedProjects([]);
    setSelectedMaterials([]);
    setSelectedStartingMaterials([]);
    setProcessType('SYN');
    setSelectedResearcher(null);
    setSelectedReportingCategory(null);
    setSelectedTechnique(null);
    setSelectedInstrument(null);
    setRecipeTemplate(null);
    setAvailableTemplates([]);
    setDescription('');
    setSelectedHolder(null);
    setSelectedFile(null);
    setStartingMaterialError(false);
    setMaterialError(false);
    setResearcherError(false);
    setProjectError(false);
    setUpdatedMaterials(null); // Reset updated materials when dialog closes
    setPristineChoiceDialogOpen(false);
    setPristineSample(null);
    onClose();
  };

  // Shared helper function to create a new sample with startingMaterials and an activity
  const createNewSampleWithActivity = async () => {
    if (!effectiveResearcher || !selectedDate) {
      showSnackbar('Missing required fields', 'error');
      return;
    }

    if (selectedStartingMaterials.length === 0) {
      showSnackbar('At least one startingMaterial is required', 'error');
      return;
    }

    try {
      log.debug('[AddSampleFromStartingMaterialsDialog] Creating sample from startingMaterials:', {
        startingMaterialIds: selectedStartingMaterials.map((p) => p.id),
        startingMaterialLabels: selectedStartingMaterials.map((p) => p.sampleLabel),
        processType: processType,
      });

      // Create the sample from startingMaterials
      const createRequest: CombineStartingMaterialsRequest = {
        startingMaterials: selectedStartingMaterials.map((p) => p.id!.toString()), // Use IDs as strings
        projects: selectedProjects.map((p) => p.id!),
        creationDate: selectedDate,
        userId: effectiveResearcher.id!,
        processType: processType,
        techniqueId: selectedTechnique?.id,
        instrumentId: nullIfBlank(selectedInstrument?.id),
        recipeTemplateName: recipeTemplate?.name,
        materialIds: processType === 'SYN' && selectedMaterials.length > 0 ? selectedMaterials.map((m) => m.id!) : [],
        sampleHolder: selectedHolder || undefined,
        reportingCategoryId: selectedReportingCategory?.id ?? undefined,
        description: description || undefined,
      };

      const newSample = await backend.sample.combineStartingMaterials(createRequest);
      log.debug('[AddSampleFromStartingMaterialsDialog] Sample created from startingMaterials:', newSample);

      showSnackbar(`Sample ${newSample.sampleLabel} added successfully`, 'success');

      // Reset and close the dialog
      handleFormReset();

      // Navigate to the newly created sample, we got it from the backend so it's guaranteed to have an ID.
      const targetSampleSlug = encodeURIComponent(newSample.sampleLabel ?? newSample.id!);
      router.push(`${systemConfig?.basePath ?? ''}/samples/${targetSampleSlug}`);
    } catch (error) {
      log.error('[AddSampleFromStartingMaterialsDialog] Failed to create sample from startingMaterials:', error);
      showSnackbar(getProblemDetail(error) ?? 'Failed to create sample from starting materials.', 'error');
    }
  };

  // Handle submit
  const handleSubmit = async () => {
    let hasError = false;

    if (!effectiveResearcher) {
      setResearcherError(true);
      hasError = true;
    } else {
      setResearcherError(false);
    }

    if (!allowCreateWithoutProject && selectedProjects.length === 0) {
      setProjectError(true);
      hasError = true;
    } else {
      setProjectError(false);
    }

    // Material is only required for synthesis
    if (processType === 'SYN' && selectedMaterials.length === 0) {
      setMaterialError(true);
      hasError = true;
    } else {
      setMaterialError(false);
    }

    if (selectedStartingMaterials.length === 0) {
      setStartingMaterialError(true);
      hasError = true;
    } else {
      setStartingMaterialError(false);
    }

    if (hasError) {
      showSnackbar('Please fill out all required fields.', 'error');
      return;
    }

    const multErr = validateProjectIdsForSampleMultiplicity(
      multiplicityPolicy,
      selectedProjects.map((p) => p.id),
      resolveProjectDto
    );
    if (multErr) {
      showSnackbar(multErr, 'error');
      return;
    }

    // For synthesis: Check if using a single pristine substrate as the only startingMaterial
    // Offer to start processing on the existing sample instead
    if (processType === 'SYN' && selectedStartingMaterials.length === 1) {
      const sample = selectedStartingMaterials[0];
      const hasMaterials = sample.materialIds && sample.materialIds.length > 0;
      const hasBeenUsedAsStartingMaterial = sample.sampleAsSubstrate !== undefined && sample.sampleAsSubstrate !== null;

      if (!hasMaterials && !hasBeenUsedAsStartingMaterial) {
        // Show three-option dialog
        setPristineSample(sample);
        setPristineChoiceDialogOpen(true);
        return; // Wait for user choice
      }
    }

    // Validate recipe template against startingMaterial count
    if (!isValidRecipeTemplateForStartingMaterials(recipeTemplate, selectedStartingMaterials.length)) {
      setRecipeTemplateError(true);
      hasError = true;
      const errorMessage =
        selectedStartingMaterials.length === 1
          ? 'Please select a valid recipe template or leave it empty for single startingMaterial processes.'
          : `Recipe template is required for ${selectedStartingMaterials.length} startingMaterials. Please select a template that supports this many startingMaterials.`;
      showSnackbar(errorMessage, 'error');
      return;
    } else {
      setRecipeTemplateError(false);
    }

    if (hasError) {
      showSnackbar('Please fill out all required fields.', 'error');
      return;
    }

    // Create new sample with activity
    await createNewSampleWithActivity();
  };

  // Handle "Add New Sample" option from pristine substrate dialog
  const handleCreateNewSample = async () => {
    // Add new sample with activity (dialog is closed by onClick handler)
    await createNewSampleWithActivity();
  };

  // Handle "Process Existing" option from pristine substrate dialog
  const handleProcessExistingSample = async () => {
    if (!pristineSample || !effectiveResearcher || !selectedDate) {
      showSnackbar('Missing required fields for processing activity', 'error');
      return;
    }

    try {
      log.debug('[AddSampleFromStartingMaterialsDialog] Creating processing activity on existing sample:', {
        sampleId: pristineSample.id,
        sampleLabel: pristineSample.sampleLabel,
        processType: processType,
      });

      // Create the activity directly using the data already collected in this form
      const newActivity: SampleActivityDto = {
        sampleId: pristineSample.id,
        userId: effectiveResearcher.id,
        date: selectedDate,
        technique: nullIfBlank(selectedTechnique?.id),
        instrument: nullIfBlank(selectedInstrument?.id),
        desc: description || '',
        recipeTemplate: recipeTemplate?.name,
        recipe: recipeTemplate || undefined,
        type: processType as ActivityTypeDto,
        category: ActivityCategoryDto.Processing,
        materials:
          processType === 'SYN' && selectedMaterials.length > 0
            ? selectedMaterials.map((m) => ({
                id: m.id,
                name: m.label,
              }))
            : undefined,
      };

      const created = await backend.sampleActivity.create(newActivity);
      log.debug('[AddSampleFromStartingMaterialsDialog] Processing activity created on existing sample:', created);
      showSnackbar(`Processing activity created for ${pristineSample.sampleLabel}`, 'success');

      // Close this dialog
      handleFormReset();

      // Navigate to the newly created sample, we got it from the backend so it's guaranteed to have an ID.
      const targetSampleSlug = encodeURIComponent(pristineSample.sampleLabel ?? pristineSample.id!);
      router.push(`${systemConfig?.basePath ?? ''}/samples/${targetSampleSlug}`);
    } catch (error) {
      log.error('[AddSampleFromStartingMaterialsDialog] Failed to create processing activity:', error);
      showSnackbar(getProblemDetail(error) ?? 'Failed to create processing activity.', 'error');
    }
  };

  return (
    <>
      <StandardDialog
        open={open}
        onClose={handleFormReset}
        title="Create Sample from Several Starting Materials"
        maxWidth="md"
        onSubmit={handleSubmit}
        submitText="Add Sample"
        helpDocKey="dialog.combineStartingMaterials"
        hasUnsavedChanges={hasUnsavedChanges}
        unsavedChangesMessage="You have unsaved changes to this sample. Are you sure you want to discard them?"
      >
        {/* StartingMaterial Samples Selection */}
        <Box>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <Typography variant="subtitle2" color={startingMaterialError ? 'error' : 'text.secondary'}>
              StartingMaterial Sample(s) *
            </Typography>
            <Button variant="outlined" size="small" onClick={handleStartingMaterialLookup}>
              Add StartingMaterials
            </Button>
          </Box>

          {selectedStartingMaterials.length > 0 ? (
            <Stack spacing={1}>
              {selectedStartingMaterials.map((startingMaterial, index) => (
                <Paper key={startingMaterial.id} variant="outlined" sx={{ p: 1.5 }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box flex={1}>
                      <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                        {selectedStartingMaterials.length > 1 ? (
                          <Chip label={`#${index + 1}`} size="small" color="primary" sx={{ height: 20 }} />
                        ) : null}
                        <Typography variant="body2" fontWeight={500}>
                          {startingMaterial.sampleLabel}
                        </Typography>
                        {startingMaterial.refSampleLabel ? (
                          <Typography variant="caption" color="text.secondary">
                            ({startingMaterial.refSampleLabel})
                          </Typography>
                        ) : null}
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        Material:{' '}
                        {startingMaterial.materials && startingMaterial.materials.length > 0
                          ? startingMaterial.materials.join(', ')
                          : startingMaterial.substrateMaterials && startingMaterial.substrateMaterials.length > 0
                            ? startingMaterial.substrateMaterials.join(', ')
                            : '(not specified)'}
                      </Typography>
                    </Box>
                    <Box display="flex" gap={0.5}>
                      {selectedStartingMaterials.length > 1 ? (
                        <>
                          <IconButton
                            size="small"
                            onClick={() => handleMoveStartingMaterialUp(index)}
                            disabled={index === 0}
                            title="Move up"
                          >
                            <ArrowUpwardIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleMoveStartingMaterialDown(index)}
                            disabled={index === selectedStartingMaterials.length - 1}
                            title="Move down"
                          >
                            <ArrowDownwardIcon fontSize="small" />
                          </IconButton>
                        </>
                      ) : null}
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveStartingMaterial(index)}
                        color="error"
                        title="Remove"
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                </Paper>
              ))}
            </Stack>
          ) : (
            <Typography
              variant="body2"
              color={startingMaterialError ? 'error' : 'text.secondary'}
              sx={{ fontStyle: 'italic' }}
            >
              No startingMaterial samples selected. Click &ldquo;Add StartingMaterials&rdquo; to add samples.
            </Typography>
          )}
          {selectedStartingMaterials.length > 0 ? (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              {selectedStartingMaterials.length > 1
                ? `${selectedStartingMaterials.length} startingMaterials selected. Order matters for recipes that use specific positions.`
                : 'You can add more startingMaterial samples if needed for bulk preparation or multi-source processes.'}
            </Typography>
          ) : null}
        </Box>

        <Divider />

        {/* Process Type Selection */}
        <FormControl component="fieldset">
          <FormLabel component="legend">Process Type *</FormLabel>
          <RadioGroup row value={processType} onChange={handleProcessTypeChange}>
            <FormControlLabel value="PREP" control={<Radio />} label="Preparation" />
            <FormControlLabel value="SYN" control={<Radio />} label="Synthesis" />
          </RadioGroup>
        </FormControl>

        <Divider />

        {/* Researcher and Date */}
        <Box display="flex" gap={2}>
          <FormAutocomplete
            options={filteredStaff ?? []}
            getOptionLabel={(option) => option.fullName || ''}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                {option.fullName}
              </li>
            )}
            value={effectiveResearcher}
            onChange={(_, newValue) => {
              setSelectedResearcher(newValue || null);
              // Reset technique/instrument/recipe when changing researcher
              setSelectedTechnique(null);
              setSelectedInstrument(null);
              setRecipeTemplate(null);
              setAvailableTemplates([]);
              markAsInteracted();
            }}
            label="Added By"
            required
            error={researcherError}
            size="small"
            autoSelect
            autoHighlight
            sx={{ flex: 1 }}
          />
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Date"
              value={selectedDate}
              onChange={(newValue) => {
                setSelectedDate(newValue);
                markAsInteracted();
              }}
              slotProps={{ textField: { size: 'small', fullWidth: true, sx: { flex: 1 } } }}
            />
          </LocalizationProvider>
        </Box>

        <Divider />

        {/* Technique, Instrument, Recipe Template Selection */}
        <Box display="flex" gap={2}>
          <FormAutocomplete
            options={techniqueOptions}
            getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
            value={selectedTechnique}
            onChange={handleTechniqueChange}
            label="Technique"
            size="small"
            autoSelect
            autoHighlight
            sx={{ flex: 1 }}
            disabled={!effectiveResearcher}
          />
          <FormAutocomplete
            options={instrumentOptions}
            getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
            value={selectedInstrument}
            onChange={handleInstrumentChange}
            label="Instrument"
            size="small"
            autoSelect
            autoHighlight
            sx={{ flex: 1 }}
            disabled={!selectedTechnique}
          />
          {/* Recipe Template Selection */}
          {/* Show if templates are available AND (instrument is selected OR any template doesn't use instrument) */}
          {/* Required for 2+ startingMaterials, optional for single startingMaterial */}
          {availableTemplates.length > 0 &&
          (selectedInstrument || availableTemplates.some((t) => t.usesInstrument === false)) ? (
            <FormAutocomplete
              options={availableTemplates.map((t) => t.name || '').filter((name) => !!name)}
              value={recipeTemplate?.name || ''}
              onChange={handleRecipeTemplateChange}
              label="Recipe Template"
              required={selectedStartingMaterials.length > 1}
              error={recipeTemplateError}
              size="small"
              autoSelect
              autoHighlight
              sx={{ flex: 1 }}
            />
          ) : null}
        </Box>

        <Divider />

        {/* Material (what's being synthesized) - only show for synthesis */}
        {processType === 'SYN' ? (
          <Box display="flex" gap={1}>
            <FormAutocomplete
              options={materialOptions ?? []}
              getOptionLabel={(option) => option.label}
              filterOptions={(opts, params) =>
                filterAutocompleteOptionsByMatchRank(opts, params, (o) => String(o.label ?? ''))
              }
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={selectedMaterials[0] ?? null}
              onChange={(_, newValue) => {
                setSelectedMaterials(newValue ? [newValue] : []);
                markAsInteracted();
              }}
              autoSelect
              autoHighlight
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  {option.label}
                </li>
              )}
              label="Material being synthesized"
              required
              error={materialError}
              size="small"
              sx={{ flex: 1 }}
            />
            {canCreateMaterial ? <MaterialCreateAddButton onClick={() => setIsMaterialDialogOpen(true)} /> : null}
          </Box>
        ) : null}

        <Divider />

        {/* Project and Reporting Category */}
        <Box display="flex" gap={2}>
          <Box flex={2}>
            <EntityAutocompleteWithLookup
              label={`${allowMultiProject ? 'Project(s)' : 'Project'}${allowCreateWithoutProject ? LABEL_SUFFIX_OPTIONAL : LABEL_SUFFIX_REQUIRED}`}
              options={projectOptions}
              value={selectedProjects}
              onChange={(newValue) => {
                // Policy can make it a single select field, or a multi-select field.
                // We need to handle both cases.
                const incomingProjects = Array.isArray(newValue) ? newValue : newValue != null ? [newValue] : [];
                const { nextProjects, multiplicityMessage } = enforceProjectMultiplicitySelection(
                  multiplicityPolicy,
                  incomingProjects,
                  resolveProjectDto
                );
                setSelectedProjects(nextProjects);
                if (nextProjects.length > 0) setProjectError(false);
                setProjectMultiplicityMessage(multiplicityMessage);
                markAsInteracted();
              }}
              onLookupClick={handleProjectLookup}
              multiple={allowMultiProject}
              lookupTooltip={allowMultiProject ? 'Search and select projects' : 'Search and select a project'}
              error={projectError}
              helperText={
                projectError
                  ? 'At least one project is required.'
                  : (projectMultiplicityMessage ?? multiplicityHint ?? undefined)
              }
            />
          </Box>
          <FormAutocomplete
            options={options.sampleReportingCategory ?? []}
            getOptionLabel={(option) => option.label || option.code || ''}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={selectedReportingCategory}
            onChange={(_, newValue) => {
              setSelectedReportingCategory(newValue);
              markAsInteracted();
            }}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                {option.label || option.code}
              </li>
            )}
            label="Reporting Category"
            size="small"
            autoSelect
            autoHighlight
            sx={{ flex: 1 }}
          />
        </Box>

        <Divider />

        {/* Sample Holder - only show for synthesis */}
        {processType === 'SYN' ? (
          <FormAutocomplete
            freeSolo
            options={(options.sampleDropdownChoices?.sampleHolders ?? []).filter(
              (option) => option && option.trim() !== ''
            )}
            value={selectedHolder}
            onChange={(_, newValue) => {
              setSelectedHolder(newValue || null);
              markAsInteracted();
            }}
            onInputChange={(_, newInputValue) => {
              setSelectedHolder(newInputValue);
              markAsInteracted();
            }}
            label="Sample Holder"
            size="small"
          />
        ) : null}

        <Divider />
        {/* Description */}
        <Box width="100%">
          <Typography variant="subtitle2" gutterBottom>
            Notes
          </Typography>
          <Box width="100%">
            <RichTextEditor
              value={description}
              onChange={(value) => {
                setDescription(value);
                markAsInteracted();
              }}
            />
          </Box>
        </Box>

        <Divider />

        {/* Attach image button */}
        <Box display="flex" justifyContent="flex-start">
          <Button variant="contained" color="primary" size="medium" startIcon={<AttachFileIcon />} component="label">
            Attach Image
            <input type="file" hidden accept="image/*" onChange={handleFileChange} />
          </Button>
        </Box>

        {/* Display selected file if any */}
        {selectedFile ? (
          <Box
            mt={1}
            p={2}
            border="1px solid #ccc"
            borderRadius={2}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            width="100%"
          >
            <Box display="flex" alignItems="center" gap={2}>
              <Box
                sx={{
                  position: 'relative',
                  width: 80,
                  height: 80,
                  borderRadius: 1,
                  overflow: 'hidden',
                }}
              >
                <Image
                  src={URL.createObjectURL(selectedFile)}
                  alt="Preview"
                  layout="fill"
                  objectFit="cover"
                  unoptimized
                />
              </Box>
              <Typography variant="body2">{selectedFile.name}</Typography>
            </Box>

            <Box display="flex" gap={1}>
              <Button
                size="small"
                variant="outlined"
                component="a"
                href={URL.createObjectURL(selectedFile)}
                download={selectedFile.name}
              >
                Download
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="error"
                onClick={() => {
                  setSelectedFile(null);
                  markAsInteracted();
                }}
              >
                <DeleteIcon />
              </Button>
            </Box>
          </Box>
        ) : null}
      </StandardDialog>

      <MaterialCreateDialog
        open={isMaterialDialogOpen}
        forSubstrate={false}
        onClose={() => setIsMaterialDialogOpen(false)}
        onCreated={(savedMaterial, freshMaterials) => {
          const materialOptionsFromFresh = toNonSubstrateMaterialPicks(freshMaterials);
          setUpdatedMaterials(materialOptionsFromFresh);
          const createdMaterial = materialOptionsFromFresh.find((m) => m.id === savedMaterial.id);
          if (createdMaterial) {
            setSelectedMaterials([createdMaterial]);
          }
          showSnackbar('New material created successfully', 'success');
        }}
      />

      {/* Three-option dialog for single pristine substrate */}
      <Dialog
        open={pristineChoiceDialogOpen}
        onClose={() => setPristineChoiceDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Single Pristine Substrate Detected</DialogTitle>
        <DialogContent>
          <Typography>
            You are using a single pristine substrate <strong>{pristineSample?.sampleLabel}</strong> as a
            startingMaterial.
          </Typography>
          <Typography sx={{ mt: 2 }}>What would you like to do?</Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
          <Button onClick={() => setPristineChoiceDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Box display="flex" gap={1}>
            <Button
              onClick={() => {
                setPristineChoiceDialogOpen(false);
                handleCreateNewSample();
              }}
              variant="outlined"
              color="primary"
            >
              Add New Sample
            </Button>
            <Button
              onClick={() => {
                setPristineChoiceDialogOpen(false);
                handleProcessExistingSample();
              }}
              variant="contained"
              color="primary"
            >
              Process Existing
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};

export { CombineStartingMaterialsDialog };
